import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AnalyzeModal from "@/components/AnalyzeModal";
import IdeaBankModal from "@/components/IdeaBankModal";
import CollaboratorsCard from "@/components/CollaboratorsCard";
import CharacterProfileEditor from "@/components/CharacterProfileEditor";
import ChannelSettingsPanel from "@/components/ChannelSettingsPanel";
import {
  getActiveChannelDetail,
  refreshActiveChannel,
  analyzeChannelForIdeas,
  getYouTubeLinkStatus,
  startYouTubeLink,
} from "@/lib/api";

function fmtHandle(raw) {
  // YouTube @handles can ONLY contain letters, digits, dot, underscore,
  // and hyphen. If we ever get a value with a space, apostrophe, or
  // other punctuation it means the value is actually the channel NAME
  // mistakenly stored as the handle (see Round 20 fix). Hide it
  // rather than render the misleading "@Bella's Play Club".
  if (!raw) return "";
  const clean = String(raw).replace(/^@/, "").trim();
  if (!clean) return "";
  if (!/^[A-Za-z0-9_.\-]{3,}$/.test(clean)) return "";
  return clean;
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num);
}

function fmtCurrency(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num) || num === 0) return "—";
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-zinc-950 rounded-sm p-5"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-1">
        {title}
      </div>
      {subtitle && (
        <div className="text-xs text-zinc-500 mb-4">{subtitle}</div>
      )}
      <div className="h-64">{children}</div>
    </div>
  );
}

function HeadStat({ label, value, sub }) {
  return (
    <div className="bg-black border border-zinc-900 px-4 py-3 rounded-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-white leading-none">
        {value}
      </div>
      {sub && (
        <div className="font-mono text-[10px] text-zinc-400 mt-1.5">{sub}</div>
      )}
    </div>
  );
}

function parseDur(iso) {
  if (!iso) return 0;
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  return (+m[1] || 0) * 3600 + (+m[2] || 0) * 60 + (+m[3] || 0);
}

function fmtDur(sec) {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ActiveChannelDetail() {
  const { channelId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [ideaBankOpen, setIdeaBankOpen] = useState(false);
  // Character profile lives in `channel_analyses` keyed by handle —
  // separate from the active-channel snapshot data. We lazy-load it
  // when the detail page mounts so the user can manage characters
  // (name, refs, species, outfit) without leaving Phase 4.
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  // Tracks the last `channelId` we've already auto-refreshed for so the
  // background refresh fires exactly once per channel visit — even if
  // the user navigates A → B → A, both visits refresh.
  const autoRefreshedKeyRef = useRef("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await getActiveChannelDetail(channelId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load channel");
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Auto-refresh stats every time the user navigates to a channel
   * detail page so they always see live numbers without clicking the
   * ↻ button. The initial `load()` renders the cached snapshot
   * instantly; this effect then triggers a server-side refresh from
   * YouTube and re-fetches the detail when it completes. Guarded
   * by a ref so we don't re-fire after the refresh updates `detail`.
   */
  useEffect(() => {
    if (!channelId) return undefined;
    if (autoRefreshedKeyRef.current === channelId) return undefined;
    autoRefreshedKeyRef.current = channelId;
    let cancelled = false;
    (async () => {
      try {
        await refreshActiveChannel(channelId);
        if (cancelled) return;
        const fresh = await getActiveChannelDetail(channelId);
        if (!cancelled) setDetail(fresh);
      } catch {
        // Silent — stale cached data is still useful.
      }
    })();
    return () => { cancelled = true; };
  }, [channelId]);

  // Load the cached character profile once we know the handle. Uses
  // `analyzeChannelForIdeas` because it returns the full analysis
  // doc (with `visuals.characters[]`) and is no-op cached on the
  // backend — so subsequent visits don't re-spend on GPT calls.
  const handle = detail?.handle || "";
  useEffect(() => {
    if (!handle) return undefined;
    let cancelled = false;
    (async () => {
      setAnalysisLoading(true);
      try {
        const res = await analyzeChannelForIdeas(handle);
        if (!cancelled) setAnalysis(res?.analysis || null);
      } catch {
        if (!cancelled) setAnalysis(null);
      } finally {
        if (!cancelled) setAnalysisLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  // Tracks whether the user's Google/YouTube OAuth session is linked
  // AND — if so — whether the linked YT channel_id matches THIS
  // active-channel's channel_id. Drives the "Connect to YouTube" vs
  // "✓ Connected" button in the header CTA stack.
  const [ytLinkStatus, setYtLinkStatus] = useState(null);
  const [ytLinkLoading, setYtLinkLoading] = useState(false);

  const loadYtStatus = useCallback(async () => {
    try {
      const res = await getYouTubeLinkStatus();
      setYtLinkStatus(res || null);
    } catch {
      setYtLinkStatus(null);
    }
  }, []);

  useEffect(() => { loadYtStatus(); }, [loadYtStatus]);

  // Surface success/error from the OAuth callback (redirects back to
  // this same page with `?yt_link=success` when the user linked from
  // here). Toast + strip the query so refresh doesn't re-fire it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("yt_link");
    if (!flag) return;
    if (flag === "success") {
      toast.success("YouTube channel linked");
      loadYtStatus();
    } else {
      const detail_ = params.get("detail") || params.get("reason") || "unknown error";
      toast.error(`YouTube link failed: ${detail_}`);
    }
    params.delete("yt_link");
    params.delete("reason");
    params.delete("detail");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? `?${qs}` : ""),
    );
  }, [loadYtStatus]);

  const onConnectYouTube = async () => {
    if (ytLinkLoading) return;
    setYtLinkLoading(true);
    try {
      const next = `/active-channels/${encodeURIComponent(channelId)}`;
      const url = await startYouTubeLink({ next });
      // Full-page navigate — OAuth flows need a top-level window.
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start YouTube link");
      setYtLinkLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshActiveChannel(channelId);
      toast.success("Stats refreshed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const onNextVideo = () => {
    // Open the Idea Bank modal — same flow as the dashboard cards.
    setIdeaBankOpen(true);
  };

  if (loading) {
    return (
      <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto">
        <div className="font-mono text-sm text-zinc-500" data-testid="acd-loading">
          [recon] loading<span className="blink">_</span>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto">
        <div className="font-mono text-sm text-red-400" data-testid="acd-error">
          Channel not found.
        </div>
        <Link to="/active-channels" className="text-yellow-400 underline">
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  const r = detail.rollup || {};
  const snaps = detail.snapshots || [];
  const subsSeries = snaps
    .filter((s) => s.subscribers !== null && s.subscribers !== undefined)
    .map((s) => ({ date: s.date_utc, value: s.subscribers }));
  const viewsSeries = snaps.map((s) => ({
    date: s.date_utc,
    value: s.total_views,
  }));
  const cadence = Object.entries(detail.videos_per_month || {}).map(
    ([month, count]) => ({ month, count }),
  );

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="active-channel-detail">
      {analyzeOpen && (
        <AnalyzeModal
          channel={detail}
          onClose={() => setAnalyzeOpen(false)}
        />
      )}
      {ideaBankOpen && (
        <IdeaBankModal
          channel={detail}
          onClose={() => setIdeaBankOpen(false)}
        />
      )}

      <Link
        to="/active-channels"
        className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-yellow-400 mb-4 inline-block"
      >
        ← Phase 4 · Active channels
      </Link>

      <div className="flex items-start gap-5 mb-8">
        {detail.thumbnail ? (
          <img
            src={detail.thumbnail}
            alt=""
            className="h-20 w-20 rounded-full border border-zinc-800 shrink-0"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-20 w-20 rounded-full bg-zinc-900 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white leading-tight">
            {detail.name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {fmtHandle(detail.handle) && (
              <div className="text-sm text-zinc-300 font-mono">
                @{fmtHandle(detail.handle)}
              </div>
            )}
            {r.niche_label && (
              <span
                data-testid="acd-niche-badge"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-yellow-400/15 border border-yellow-400/40 font-mono text-[10px] uppercase tracking-wider text-yellow-300"
              >
                <span className="text-yellow-500">◆</span>
                {r.niche_label}
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-400 mt-2 flex flex-wrap gap-3">
            <span>Created {fmtDate(detail.channel_created)}</span>
            <span>·</span>
            <span>Tracked since {fmtDate(detail.added_at)}</span>
            <span>·</span>
            <span>Synced {fmtDate(detail.last_snapshot_at)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={onNextVideo}
            data-testid="acd-next-video"
            className="font-display text-sm font-black uppercase tracking-wide bg-emerald-400 text-black hover:bg-emerald-300 px-4 py-2 rounded-sm transition-colors"
          >
            ▶ Next video
          </button>
          <Link
            to={`/active-channels/${detail.channel_id}/roll-your-own`}
            data-testid="acd-roll-your-own"
            className="text-center font-display text-sm font-black uppercase tracking-wide bg-fuchsia-500 text-black hover:bg-fuchsia-400 px-4 py-2 rounded-sm transition-colors"
          >
            🎨 Roll your own
          </Link>
          <button
            onClick={() => setAnalyzeOpen(true)}
            data-testid="acd-analyze"
            className="font-display text-sm font-black uppercase tracking-wide bg-yellow-400/10 text-yellow-300 border border-yellow-400/50 hover:bg-yellow-400/20 px-4 py-2 rounded-sm transition-colors"
          >
            ▸ Analyze
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="acd-refresh"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 disabled:text-zinc-600 transition-colors"
          >
            {refreshing ? "refreshing…" : "↻ refresh stats"}
          </button>
          {(() => {
            const isThisChannelLinked =
              !!(ytLinkStatus?.linked
                 && ytLinkStatus?.channel_id
                 && ytLinkStatus.channel_id === detail.channel_id);
            if (isThisChannelLinked) {
              return (
                <div
                  data-testid="acd-yt-connected"
                  title={`Linked as ${ytLinkStatus.channel_title || "this channel"} — YT Maker can publish videos to it.`}
                  className="inline-flex items-center justify-center gap-1.5 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 border border-emerald-400/50 bg-emerald-400/10 px-3 py-1.5 rounded-sm"
                >
                  <span className="text-emerald-400">✓</span>
                  connected to youtube
                </div>
              );
            }
            const otherLinked = !!(ytLinkStatus?.linked && ytLinkStatus?.channel_id);
            return (
              <button
                type="button"
                onClick={onConnectYouTube}
                disabled={ytLinkLoading}
                data-testid="acd-yt-connect"
                title={otherLinked
                  ? `Currently linked to "${ytLinkStatus.channel_title || "another channel"}". Clicking will re-link YT Maker to THIS channel — sign in with the Google account that owns @${fmtHandle(detail.handle) || "this channel"}.`
                  : "Sign in with the Google account that owns this channel so YT Maker can publish videos directly to it."}
                className="inline-flex items-center justify-center gap-1.5 text-center font-mono text-[10px] uppercase tracking-[0.25em] bg-red-500 hover:bg-red-400 text-black font-black px-3 py-1.5 rounded-sm disabled:opacity-50 transition-colors"
              >
                <span className="text-black">▶</span>
                {ytLinkLoading
                  ? "opening…"
                  : otherLinked ? "reconnect to youtube" : "connect to youtube"}
              </button>
            );
          })()}
          <a
            href={`https://www.youtube.com/channel/${detail.channel_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 hover:text-yellow-400 transition-colors"
          >
            open on youtube ↗
          </a>
        </div>
      </div>

      <div className="mb-6">
        <CollaboratorsCard channelId={detail.channel_id} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <HeadStat
          label="subscribers"
          value={detail.subscribers_hidden ? "Hidden" : fmtNum(detail.subscribers)}
          sub={
            r.subs_delta_7d === null || r.subs_delta_7d === undefined
              ? "no Δ yet"
              : `${r.subs_delta_7d >= 0 ? "+" : ""}${fmtNum(r.subs_delta_7d)} · 7d`
          }
        />
        <HeadStat label="total views" value={fmtNum(detail.total_views)} />
        <HeadStat label="videos" value={fmtNum(detail.video_count)} />
        <HeadStat
          label="avg views / video"
          value={fmtNum(r.avg_views_per_video)}
        />
        <HeadStat
          label="est. monthly rev"
          value={fmtCurrency(r.est_monthly_revenue)}
          sub={`${r.niche_label || "—"} · $${r.rpm_used} RPM`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <ChartCard
          testid="acd-subs-chart"
          title="// SUBS OVER TIME"
          subtitle={
            subsSeries.length < 2
              ? "Daily snapshot starts tomorrow — chart fills in as the cron runs."
              : `${subsSeries.length} snapshot(s)`
          }
        >
          {subsSeries.length >= 2 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={subsSeries}>
                <defs>
                  <linearGradient id="subsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#facc15" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#facc15" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={fmtNum} />
                <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0a",
                    border: "1px solid #27272a",
                    fontFamily: "monospace",
                    fontSize: 12,
                  }}
                  formatter={(v) => [fmtNum(v), "subs"]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#facc15"
                  fill="url(#subsGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-600 font-mono text-xs">
              Not enough snapshots yet.
            </div>
          )}
        </ChartCard>

        <ChartCard
          testid="acd-views-chart"
          title="// TOTAL VIEWS OVER TIME"
          subtitle={
            viewsSeries.length < 2
              ? "Same — comes online with the daily cron."
              : `${viewsSeries.length} snapshot(s)`
          }
        >
          {viewsSeries.length >= 2 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={viewsSeries}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={fmtNum} />
                <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
                <Tooltip
                  contentStyle={{
                    background: "#0a0a0a",
                    border: "1px solid #27272a",
                    fontFamily: "monospace",
                    fontSize: 12,
                  }}
                  formatter={(v) => [fmtNum(v), "views"]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#22d3ee"
                  fill="url(#viewsGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-600 font-mono text-xs">
              Not enough snapshots yet.
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard
        testid="acd-cadence-chart"
        title="// VIDEOS POSTED PER MONTH"
        subtitle={`Based on the most recent ${(detail.videos || []).length} uploads`}
      >
        {cadence.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cadence}>
              <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 10 }} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} />
              <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
              <Tooltip
                contentStyle={{
                  background: "#0a0a0a",
                  border: "1px solid #27272a",
                  fontFamily: "monospace",
                  fontSize: 12,
                }}
                formatter={(v) => [v, "videos"]}
              />
              <Bar dataKey="count" fill="#facc15" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-zinc-600 font-mono text-xs">
            No uploads yet.
          </div>
        )}
      </ChartCard>

      {/* Channel-scoped character roster. Each Active Channel manages
          its OWN characters — Bella's Play Club's Shih Tzu Bella does
          NOT leak into Peaceful Years or Paws and Hacks. The editor is
          backed by `channel_analyses.visuals.characters` keyed by
          handle, same as the Channel Search analysis card. */}
      <div
        className="mt-8 border border-zinc-800 bg-zinc-950 rounded-sm p-5 space-y-3"
        data-testid="acd-character-roster"
      >
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-1">
              // CHARACTERS
            </div>
            <div className="text-xs text-zinc-500">
              Define this channel&apos;s cast — name, species, outfit, and
              reference images. These feed straight into Create Image,
              GPT Image, and the storyboard thumbnail renderer for
              every video on this channel.
            </div>
          </div>
          {analysisLoading && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              loading…
            </span>
          )}
        </div>
        {handle ? (
          <CharacterProfileEditor
            handle={handle}
            initialVisuals={analysis?.visuals}
            showAdvanced
          />
        ) : (
          <p className="text-[11px] text-zinc-500 italic">
            This channel has no @handle on record — character data is
            keyed by handle, so we can&apos;t load or edit it here. Click
            ↻ refresh stats above to pull a fresh snapshot from
            YouTube; if the handle is still missing afterwards the
            channel may have been renamed.
          </p>
        )}
      </div>

      {/* Channel-scoped defaults: style preset, post format, voice,
          default cast. This is where the user picks "Stone Age" (or
          any other named style) so every new storyboard spawned
          under this channel inherits it automatically. */}
      {handle && (
        <div className="mt-8" data-testid="acd-channel-settings">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2">
            // STATION 03 · CHANNEL BLUEPRINT
          </div>
          <p className="text-[11px] text-zinc-500 mb-3 max-w-3xl">
            Style, post format, narrator voice, and default cast that
            every new storyboard on this channel inherits automatically.
            Pick <span className="text-yellow-300">Stone Age</span> (or any
            other named style) here and every future scene + thumbnail
            rendered for {detail.title || "this channel"} will lean on it.
          </p>
          <ChannelSettingsPanel
            key={`acd-settings-${handle}`}
            handle={handle}
            channelLabel={detail.title || ""}
          />
        </div>
      )}

      <div className="mt-8 border border-zinc-800 bg-zinc-950 rounded-sm" data-testid="acd-videos-table">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-1">
              // RECENT VIDEOS
            </div>
            <div className="text-xs text-zinc-500">
              {(detail.videos || []).length} most recent uploads — view counts as of last snapshot.
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-zinc-300 font-mono text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Title</th>
                <th className="text-right px-3 py-2.5">Views</th>
                <th className="text-right px-3 py-2.5">Likes</th>
                <th className="text-right px-3 py-2.5">Length</th>
                <th className="text-right px-4 py-2.5">Posted</th>
              </tr>
            </thead>
            <tbody>
              {(detail.videos || []).map((v) => (
                <tr
                  key={v.video_id}
                  className="border-t border-zinc-900 hover:bg-zinc-900/40"
                  data-testid={`acd-video-row-${v.video_id}`}
                >
                  <td className="px-4 py-2.5">
                    <a
                      href={`https://www.youtube.com/watch?v=${v.video_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-100 hover:text-yellow-400 transition-colors"
                    >
                      {v.title}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-200 font-mono tabular-nums">
                    {fmtNum(v.views)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-400 font-mono tabular-nums">
                    {fmtNum(v.likes)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-500 font-mono">
                    {fmtDur(parseDur(v.duration))}
                  </td>
                  <td className="px-4 py-2.5 text-right text-zinc-500 font-mono">
                    {fmtDate(v.published_at)}
                  </td>
                </tr>
              ))}
              {(detail.videos || []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                    No videos uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
